/* eslint-disable camelcase */
import { useState } from "react";

import { useQuery, useMutation, queryCache } from "react-query";

import api from "manager/lib/api";
import { usePermissions } from "shared/hooks";

const GET_INTEGRATION_STATUSES = "getIntegrationStatuses";

const GET_COSIGN_CONNECT_INFO = "getCosignConnectInfo";

const GET_COSIGN_PROPERTIES = "getCosignProperties";

const GET_COSIGN_PROPERTY_MAPPINGS = "getCosignPropertyMappings";

export const INTEGRATION_QUERY_KEYS = Object.freeze({
  getIntegrationStatuses: () => [GET_INTEGRATION_STATUSES],
  getCosignConnectInfo: () => [GET_COSIGN_CONNECT_INFO],
  getCosignProperties: () => [GET_COSIGN_PROPERTIES],
  getCosignPropertyMappings: () => [GET_COSIGN_PROPERTY_MAPPINGS],
});

export interface ICosignConnectInfo {
  hasUuid: boolean;
  maskedUuid: string | null;
  pmStatus: string | null;
  isConnected: boolean;
}

export interface ICosignProperty {
  uuid: string;
  name?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  status?: string;
}

export interface ICreateCosignPropertyPayload {
  vero_property_id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  units: number;
}

export interface IMapExistingCosignPropertyPayload {
  vero_property_id: number;
  cosign_property_uuid: string;
}

/** Row in Section 1 — VERO properties with an active Cosign mapping (PRODUCT-988). */
export interface ICosignMappedProperty {
  mappingId: number;
  veroPropertyId: number;
  name: string;
  address: string;
  cosignPropertyName: string | null;
  cosignPropertyUuid: string;
  propertyManagerUuid: string | null;
  cosignStatus: string | null;
  isActive: boolean;
}

/** Row in Section 2 — VERO properties without a Cosign mapping (PRODUCT-988 / 990 handoff). */
export interface ICosignUnmappedProperty {
  veroPropertyId: number;
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  numberOfUnits: number;
  fullAddress: string;
}

/** Payload for GET /api/cosign/property-mappings/ (PRODUCT-988). */
export interface ICosignPropertyMappings {
  propertyManagerUuid: string | null;
  maskedPropertyManagerUuid: string | null;
  pmStatus: string | null;
  cosignEnabledCount: number;
  otherVeroCount: number;
  cosignEnabledProperties: ICosignMappedProperty[];
  otherVeroProperties: ICosignUnmappedProperty[];
}

export const useGetIntegrationStatuses = (config = {}) => {
  const { data, isLoading } = useQuery(
    INTEGRATION_QUERY_KEYS.getIntegrationStatuses(),
    api.getIntegrationStatuses,
    {
      ...config,
      retry: false,
    }
  );
  return {
    integrationStatuses: data,
    isIntegrationStatusesLoading: isLoading,
  };
};

export const usePollIntegrationStatuses = (interval: number) => {
  const [isPollingActive, setPollingActive] = useState(false);

  const {
    data,
    error: integrationStatusesError,
    isLoading,
    isFetching,
    isError: isPollIntegrationStatusesError,
  } = useQuery(
    INTEGRATION_QUERY_KEYS.getIntegrationStatuses(),
    api.getIntegrationStatuses,
    {
      refetchInterval: isPollingActive ? interval : false,
    }
  );

  return {
    startPolling: () => setPollingActive(true),
    stopPolling: () => setPollingActive(false),
    isLoading,
    isFetching,
    isPollIntegrationStatusesError,
    integrationStatuses: data,
    integrationStatusesError,
    isPollingActive,
  };
};

export const useTestConnection = () => {
  const [mutate, { isLoading, error }] = useMutation(
    ({
      provider,
      endpointUrl,
      username,
      password,
    }: {
      provider: string;
      endpointUrl: string;
      username?: string;
      password?: string;
    }) => api.testConnection(provider, endpointUrl, username, password),
    {
      onError: (error) => {
        console.error("Connection test failed:", error);
      },
    }
  );

  return {
    testConnection: mutate,
    isTestingConnection: isLoading,
    testConnectionError: error,
  };
};

export const useCosignConnectInfo = (config = {}) => {
  const { data, isLoading, error } = useQuery<ICosignConnectInfo>(
    INTEGRATION_QUERY_KEYS.getCosignConnectInfo(),
    api.getCosignConnectInfo,
    {
      retry: false,
      ...config,
    }
  );
  return {
    cosignConnectInfo: data,
    isCosignConnectInfoLoading: isLoading,
    cosignConnectInfoError: error,
  };
};

/**
 * Whether the current user can open the Cosign property-mapping dashboard.
 * Uses connect-info (`isConnected` / enable_cosign) as the single source of truth
 * so marketplace routing and CosignDashboardGate stay aligned.
 *
 * @param config - Optional react-query config forwarded to useCosignConnectInfo.
 */
export const useCosignDashboardAccess = (config = {}) => {
  const { isIntegrationsAdmin } = usePermissions();
  const { cosignConnectInfo, isCosignConnectInfoLoading } =
    useCosignConnectInfo(config);

  return {
    canAccessCosignDashboard: Boolean(
      isIntegrationsAdmin && cosignConnectInfo?.isConnected
    ),
    isCosignDashboardAccessLoading: isCosignConnectInfoLoading,
  };
};

export const useActivateCosign = () => {
  const [mutate, { isLoading, error }] = useMutation(() => api.activateCosign(), {
    // Await invalidation so consumers that navigate in their own onSuccess do so
    // only after the integration status cache has been refreshed (react-query v2
    // runs this config-level callback before the per-call onSuccess).
    onSuccess: async () => {
      await Promise.all([
        queryCache.invalidateQueries(
          INTEGRATION_QUERY_KEYS.getIntegrationStatuses()
        ),
        queryCache.invalidateQueries(
          INTEGRATION_QUERY_KEYS.getCosignConnectInfo()
        ),
        queryCache.invalidateQueries(
          INTEGRATION_QUERY_KEYS.getCosignPropertyMappings()
        ),
      ]);
    },
  });

  return {
    activateCosign: mutate,
    isActivatingCosign: isLoading,
    activateCosignError: error,
  };
};

/**
 * Path B (PRODUCT-987): create a Cosign Property Manager for a new customer.
 * The backend creates the PM in Cosign, persists the returned UUID, and sets
 * enable_cosign = True, so the connect-info / integration-status caches are
 * invalidated to reflect the now-connected (Onboarding) state.
 */
export const useCreateCosignPropertyManager = () => {
  const [mutate, { isLoading, error }] = useMutation(
    (name?: string) => api.createCosignPropertyManager(name),
    {
      onSuccess: async () => {
        await Promise.all([
          queryCache.invalidateQueries(
            INTEGRATION_QUERY_KEYS.getIntegrationStatuses()
          ),
          queryCache.invalidateQueries(
            INTEGRATION_QUERY_KEYS.getCosignConnectInfo()
          ),
          queryCache.invalidateQueries(
            INTEGRATION_QUERY_KEYS.getCosignPropertyMappings()
          ),
        ]);
      },
    }
  );

  return {
    createCosignPropertyManager: mutate,
    isCreatingCosignPropertyManager: isLoading,
    createCosignPropertyManagerError: error,
    getCreateCosignPropertyManagerErrorMessage: (fallback: string) =>
      extractCosignIntegrationErrorMessage(error, fallback),
  };
};

/**
 * PRODUCT-2233: connect a Cosign Property Manager that already exists (e.g. one
 * shared with the client's other VERO companies). The backend verifies the UUID
 * with Cosign before saving and enables Cosign when the PM is ready.
 */
export const useConnectExistingCosignAccount = () => {
  const [mutate, { isLoading }] = useMutation(
    (propertyManagerUuid: string) =>
      api.connectExistingCosignAccount(propertyManagerUuid),
    {
      onSuccess: async () => {
        await Promise.all([
          queryCache.invalidateQueries(
            INTEGRATION_QUERY_KEYS.getIntegrationStatuses()
          ),
          queryCache.invalidateQueries(
            INTEGRATION_QUERY_KEYS.getCosignConnectInfo()
          ),
          queryCache.invalidateQueries(
            INTEGRATION_QUERY_KEYS.getCosignPropertyMappings()
          ),
        ]);
      },
    }
  );

  return {
    connectExistingCosignAccount: mutate,
    isConnectingExistingCosignAccount: isLoading,
  };
};

export const useCosignProperties = (config = {}) => {
  const { data, isLoading, error, refetch } = useQuery<ICosignProperty[]>(
    INTEGRATION_QUERY_KEYS.getCosignProperties(),
    api.listCosignProperties,
    {
      retry: false,
      ...config,
    }
  );
  return {
    cosignProperties: data,
    isCosignPropertiesLoading: isLoading,
    cosignPropertiesError: error,
    refetchCosignProperties: refetch,
  };
};

export const useCreateCosignProperty = () => {
  const [mutate, { isLoading, error }] = useMutation(
    (payload: ICreateCosignPropertyPayload) =>
      api.createCosignProperty(payload),
    {
      // Refresh the cached property list so duplicate checks and any mapping
      // screens reflect the property we just created instead of stale data.
      onSuccess: () =>
        Promise.all([
          queryCache.invalidateQueries(
            INTEGRATION_QUERY_KEYS.getCosignProperties()
          ),
          queryCache.invalidateQueries(
            INTEGRATION_QUERY_KEYS.getCosignPropertyMappings()
          ),
        ]),
    }
  );

  return {
    createCosignProperty: mutate,
    isCreatingCosignProperty: isLoading,
    createCosignPropertyError: error,
  };
};

export const useMapExistingCosignProperty = () => {
  const [mutate, { isLoading, error }] = useMutation(
    (payload: IMapExistingCosignPropertyPayload) =>
      api.mapExistingCosignProperty(payload),
    {
      onSuccess: () =>
        Promise.all([
          queryCache.invalidateQueries(
            INTEGRATION_QUERY_KEYS.getCosignProperties()
          ),
          queryCache.invalidateQueries(
            INTEGRATION_QUERY_KEYS.getCosignPropertyMappings()
          ),
        ]),
    }
  );

  return {
    mapExistingCosignProperty: mutate,
    isMappingExistingCosignProperty: isLoading,
    mapExistingCosignPropertyError: error,
  };
};

/**
 * Loads Cosign property mapping dashboard data for the authenticated company.
 *
 * @param config - Optional react-query config (e.g. `enabled`).
 * @returns Mapping payload, loading/error state, and a refetch helper.
 */
export const useCosignPropertyMappings = (config = {}) => {
  const { data, isLoading, error, refetch } =
    useQuery<ICosignPropertyMappings>(
      INTEGRATION_QUERY_KEYS.getCosignPropertyMappings(),
      api.getCosignPropertyMappings,
      {
        retry: false,
        ...config,
      }
    );

  return {
    cosignPropertyMappings: data,
    isCosignPropertyMappingsLoading: isLoading,
    cosignPropertyMappingsError: error,
    refetchCosignPropertyMappings: refetch,
  };
};

const extractCosignIntegrationErrorMessage = (
  error: any,
  fallback: string
) =>
  error?.errors?.error ||
  error?.errors?.nonFieldErrors?.[0] ||
  error?.message ||
  fallback;

/**
 * Updates the company Cosign property manager UUID after Cosign verification (PRODUCT-1134).
 */
export const useUpdateCosignPropertyManagerUuid = () => {
  const [mutate, { isLoading, error }] = useMutation(
    (propertyManagerUuid: string) =>
      api.updateCosignPropertyManagerUuid(propertyManagerUuid),
    {
      onSuccess: async () => {
        await Promise.all([
          queryCache.invalidateQueries(
            INTEGRATION_QUERY_KEYS.getCosignConnectInfo()
          ),
          queryCache.invalidateQueries(
            INTEGRATION_QUERY_KEYS.getCosignPropertyMappings()
          ),
        ]);
      },
    }
  );

  return {
    updateCosignPropertyManagerUuid: mutate,
    isUpdatingCosignPropertyManagerUuid: isLoading,
    updateCosignPropertyManagerUuidError: error,
    getUpdateCosignPropertyManagerUuidErrorMessage: (fallback: string) =>
      extractCosignIntegrationErrorMessage(error, fallback),
  };
};

export const useFetchLocations = () => {
  const [mutate, { isLoading, error }] = useMutation(
    ({
      companyCode,
    }: {
      companyCode: string;
    }) => api.fetchLocations(companyCode),
    {
      onError: (error) => {
        console.error("Locations fetch failed:", error);
      },
    }
  );

  return {
    fetchLocations: mutate,
    isFetchingLocations: isLoading,
    fetchLocationsError: error,
  };
};
